import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { menuApi, type MenuDraft } from '../lib/api'
import { money } from '../lib/money'
import { useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'
import { NumPad } from '../components/NumPad'
import { Sheet } from '../components/Sheet'

/** Món trong bản nháp, kèm cờ có lấy hay không. */
interface Row extends MenuDraft {
  key: string
  take: boolean
}

type Step = 'pick' | 'reading' | 'review'

export function MenuScanScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const refreshCatalog = useApp((s) => s.refreshCatalog)

  const fileRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<Step>('pick')
  const [preview, setPreview] = useState<string | null>(null)
  const [rows, setRows] = useState<Row[]>([])
  const [note, setNote] = useState<string | null>(null)
  const [editing, setEditing] = useState<Row | null>(null)
  const [saving, setSaving] = useState(false)

  async function pick(file: File) {
    setPreview(URL.createObjectURL(file))
    setStep('reading')

    try {
      const base64 = await toBase64(file)
      const res = await menuApi.scan(base64)

      setRows(
        res.products.map((p, i) => ({ ...p, key: `${i}-${p.name}`, take: true })),
      )
      setNote(res.note)
      setStep('review')

      if (res.products.length === 0) say('Không đọc được món nào từ ảnh này')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Không đọc được ảnh')
      setStep('pick')
    }
  }

  const taken = rows.filter((r) => r.take)

  async function save() {
    if (taken.length === 0) {
      say('Chưa chọn món nào')
      return
    }
    setSaving(true)
    try {
      const res = await menuApi.bulkCreate(
        taken.map(({ name, price, unit, category, quickOptions }) => ({
          name,
          price,
          unit,
          category,
          quickOptions,
        })),
      )
      await refreshCatalog()
      say(
        res.skipped > 0
          ? `Đã thêm ${res.created} món · bỏ qua ${res.skipped} món đã có`
          : `Đã thêm ${res.created} món`,
      )
      nav('/settings/products')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa lưu được')
    }
    setSaving(false)
  }

  // ── Chọn ảnh ──
  if (step === 'pick') {
    return (
      <div className="screen">
        <AppBar title="Chụp bảng giá" onBack={() => nav('/settings/products')} />
        <div className="body">
          <p className="hint">
            Chụp bảng giá hoặc tờ menu, app đọc ra danh sách món. Đọc xong bạn xem lại và sửa
            trước khi lưu — máy đọc có thể sai, nhất là chữ viết tay.
          </p>

          <div className="scan-tips">
            <div className="tip">📐 Chụp thẳng, đừng nghiêng</div>
            <div className="tip">💡 Đủ sáng, không loá đèn</div>
            <div className="tip">🔍 Chữ và giá phải đọc được bằng mắt</div>
            <div className="tip">✂️ Bảng dài thì chụp làm nhiều lần</div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) void pick(f)
            }}
          />
        </div>

        <div className="dock">
          <button
            type="button"
            className="btn btn-brand btn-lg grow"
            onClick={() => fileRef.current?.click()}
          >
            📷 Chụp bảng giá
          </button>
        </div>
      </div>
    )
  }

  // ── Đang đọc ──
  if (step === 'reading') {
    return (
      <div className="screen">
        <AppBar title="Đang đọc bảng giá" />
        <div className="body center">
          <div className="reading">
            {preview && <img className="scan-preview" src={preview} alt="Ảnh bảng giá vừa chụp" />}
            <p className="reading-text">Đang đọc…</p>
            <p className="note center-text">Mất khoảng 10–30 giây tuỳ bảng dài ngắn.</p>
          </div>
        </div>
      </div>
    )
  }

  // ── Xem lại và sửa ──
  return (
    <div className="screen">
      <AppBar
        title="Xem lại trước khi lưu"
        sub={`${taken.length}/${rows.length} món sẽ được thêm`}
        onBack={() => setStep('pick')}
      />

      <div className="body">
        {note && (
          <div className="card warn-card">
            <p>{note}</p>
          </div>
        )}

        <p className="hint">
          Bỏ tick những món không muốn thêm. Chạm vào món để sửa tên hoặc giá.{' '}
          <b>Kiểm giá kỹ</b> — một món sai giá là bán lỗ cả ngày mà không biết.
        </p>

        {rows.length === 0 ? (
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              🔍
            </div>
            <p>Không đọc được món nào. Chụp lại gần hơn và đủ sáng.</p>
          </div>
        ) : (
          <div className="list">
            {rows.map((r) => (
              <div key={r.key} className={`draft-row${r.take ? '' : ' is-off'}`}>
                <input
                  type="checkbox"
                  checked={r.take}
                  aria-label={`Lấy món ${r.name}`}
                  onChange={(e) =>
                    setRows((list) =>
                      list.map((x) => (x.key === r.key ? { ...x, take: e.target.checked } : x)),
                    )
                  }
                />
                <button type="button" className="draft-main" onClick={() => setEditing(r)}>
                  <b>{r.name}</b>
                  <small>
                    {r.category ?? 'chưa xếp nhóm'} · {r.unit}
                    {r.quickOptions ? ` · ${r.quickOptions.replace(/,/g, ' / ')}` : ''}
                  </small>
                </button>
                <span className="row-price num">{money(r.price)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dock">
        <button type="button" className="btn btn-ghost" onClick={() => setStep('pick')}>
          Chụp lại
        </button>
        <button
          type="button"
          className="btn btn-brand grow"
          onClick={save}
          disabled={saving || taken.length === 0}
        >
          {saving ? 'Đang lưu…' : `Thêm ${taken.length} món`}
        </button>
      </div>

      {editing && (
        <Sheet title={editing.name} onClose={() => setEditing(null)}>
          <label className="field">
            <span>Tên món</span>
            <input
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              maxLength={120}
            />
          </label>

          <div className="field">
            <span>Giá bán</span>
            <div className="amount-readout num">{editing.price ? money(editing.price) : '0'}</div>
            <NumPad value={editing.price} onChange={(price) => setEditing({ ...editing, price })} />
          </div>

          <div className="sheet-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
              Bỏ
            </button>
            <button
              type="button"
              className="btn btn-brand grow"
              onClick={() => {
                setRows((list) => list.map((x) => (x.key === editing.key ? editing : x)))
                setEditing(null)
              }}
            >
              Xong
            </button>
          </div>
        </Sheet>
      )}
    </div>
  )
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Không đọc được file ảnh'))
    reader.readAsDataURL(file)
  })
}
